import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, EmptyState, ConfirmModal, showToast } from "../components/primitives";
import { ConnectionTypeChip } from "../components/connections/ConnectionTypeChip";
import { ConnectionStatusPill } from "../components/connections/ConnectionStatusPill";
import { EquippedOnHires } from "../components/connections/EquippedOnHires";
import { CredentialEditor } from "../components/wrapped/CredentialEditor";
import { OperationsTable } from "../components/wrapped/OperationsTable";
import { CallLogTab } from "../components/wrapped/CallLogTab";
import { HealthIndicator } from "../components/wrapped/HealthIndicator";
import { OAuthConfigSection } from "../components/wrapped/OAuthConfigSection";
import { MappingsList } from "../components/wrapped/MappingsList";
import {
  useWrappedApi,
  useUpdateWrappedApi,
  useRemoveWrappedApi,
  useRefreshWrappedApiSpec,
  parseOperationIdsInput,
  isOAuthAuthKind,
  type WrappedApiSummary,
} from "../hooks/use-wrapped-apis";
import { useMcpEndpoints } from "../hooks/use-mcp-endpoints";
import { useAuth } from "../../lib/hooks/use-auth";
import { useChatScope } from "../hooks/use-chat-scope";

type Tab = "overview" | "operations" | "mappings" | "calls";

/**
 * `/connections/:slug` — manage one wrapped API (formerly /wrapped-apis). Sprint 16b adds tabs
 * (Overview / Operations / Call log), live health, per-operation write toggles,
 * OAuth setup, and the service-account credential mode.
 */
export function WrappedApiDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspaceId } = useAuth();
  const isOwner = user?.workspaces.find((w) => w.id === currentWorkspaceId)?.role === "owner";

  const detail = useWrappedApi(slug);
  const endpoints = useMcpEndpoints();
  const updateWrapper = useUpdateWrappedApi();
  const removeWrapper = useRemoveWrappedApi();
  const refreshSpec = useRefreshWrappedApiSpec();

  const wrapper = detail.data?.wrapper;
  const operations = detail.data?.operations ?? [];

  // Sprint 23.9 (Fix 6 / S23.9.21) — explicit chat scope for this connection.
  // Workspace-scoped chat (root agent) with a page_context anchor so the AI
  // knows which wrapped API the user is looking at.
  useChatScope(
    { kind: "workspace", label: wrapper?.name ?? "This connection" },
    wrapper ? { kind: "wrapped_api", id: slug, name: wrapper.name } : null,
    [slug, wrapper?.name],
  );

  const [tab, setTab] = useState<Tab>("overview");
  const [name, setName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (wrapper) {
      setName(wrapper.name);
      setDirty(false);
    }
  }, [wrapper?.slug, wrapper?.name]);

  const endpointUrl =
    endpoints.data?.endpoints.find((e) => e.path === `/mcp/w/${slug}`)?.url ?? `/mcp/w/${slug}`;

  if (detail.isLoading) {
    return (
      <Frame onBack={() => navigate("/connections")}>
        <Card>
          <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>Loading…</span>
          </div>
        </Card>
      </Frame>
    );
  }

  if (detail.error || !wrapper) {
    return (
      <Frame onBack={() => navigate("/connections")}>
        <Card>
          <EmptyState>This wrapped API couldn't be found.</EmptyState>
        </Card>
      </Frame>
    );
  }

  const w = wrapper;
  const isOAuth = isOAuthAuthKind(w.auth_kind);
  const isWorkspaceScope = w.auth_kind_scope === "workspace";
  const filterActive = !!(w.path_prefix_filter || w.operation_ids_filter?.length || w.tag_filter);

  async function handleSaveName() {
    try {
      await updateWrapper.mutateAsync({ slug: w.slug, name: name.trim() });
      showToast("Saved");
      setDirty(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't save", "error", true);
    }
  }

  async function handleScopeChange(scope: "per_user" | "workspace") {
    try {
      await updateWrapper.mutateAsync({ slug: w.slug, auth_kind_scope: scope });
      showToast(scope === "workspace" ? "Switched to service-account" : "Switched to per-user credentials");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't switch", "error", true);
    }
  }

  async function handleRefreshSpec() {
    try {
      const res = await refreshSpec.mutateAsync(w.slug);
      showToast(`Spec refreshed — ${res.operation_count} operations`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't refresh spec", "error", true);
    }
  }

  async function handleRemove() {
    try {
      const res = await removeWrapper.mutateAsync(w.slug);
      showToast(`Removed ${w.name} (${res.removed_tool_count} tools)`);
      navigate("/connections");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't remove", "error", true);
    }
  }

  function copyEndpoint() {
    navigator.clipboard?.writeText(endpointUrl).then(
      () => showToast("Endpoint URL copied"),
      () => showToast("Couldn't copy", "error"),
    );
  }

  return (
    <Frame onBack={() => navigate("/connections")}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <i className="ti ti-plug" style={{ fontSize: 22, color: "var(--color-text-secondary)" }} aria-hidden="true" />
        <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>{w.name}</h1>
        <ConnectionTypeChip type="wrapped" />
        <ConnectionStatusPill
          status={
            w.archived
              ? "disabled"
              : w.my_credential_set
                ? "active"
                : w.oauth_configured
                  ? "oauth-needed"
                  : "credentials-needed"
          }
        />
        {w.archived && <span style={archivedTag}>archived</span>}
      </div>
      <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", fontFamily: "var(--font-mono)", marginBottom: 16 }}>
        {w.slug} · {w.read_only_operation_count} read · {w.enabled_write_count}/{w.write_operation_count} writes enabled
        {filterActive ? ` · filtered to ${w.operation_count_after_filter} op${w.operation_count_after_filter === 1 ? "" : "s"}` : ""}
      </div>

      <div style={tabBar}>
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")}>Overview</TabButton>
        <TabButton active={tab === "operations"} onClick={() => setTab("operations")}>
          Operations ({operations.length})
        </TabButton>
        <TabButton active={tab === "mappings"} onClick={() => setTab("mappings")}>Mappings</TabButton>
        <TabButton active={tab === "calls"} onClick={() => setTab("calls")}>Call log</TabButton>
      </div>

      {tab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card>
              <SectionTitle>{isWorkspaceScope ? "Service-account credential" : "Your credential"}</SectionTitle>
              {isWorkspaceScope && !isOwner ? (
                <div style={{ fontSize: 13, color: "var(--color-text-tertiary)" }}>
                  This API uses a shared workspace credential set by an owner.
                </div>
              ) : (
                <CredentialEditor
                  slug={w.slug}
                  authKind={w.auth_kind}
                  credentialSet={w.my_credential_set}
                  credentialUsername={w.my_credential_username}
                  serviceAccount={isWorkspaceScope}
                  oauthConfigured={w.oauth_configured}
                />
              )}
            </Card>

            {isOAuth && (
              <Card>
                <SectionTitle>OAuth configuration {isOwner ? "(owner)" : ""}</SectionTitle>
                <OAuthConfigSection wrapper={w} isOwner={!!isOwner} />
              </Card>
            )}

            {isOwner && (
              <Card>
                <SectionTitle>Credential scope (owner)</SectionTitle>
                <div style={{ display: "flex", gap: 8 }}>
                  <ScopeChip active={!isWorkspaceScope} onClick={() => handleScopeChange("per_user")}>Per-user</ScopeChip>
                  <ScopeChip active={isWorkspaceScope} onClick={() => handleScopeChange("workspace")}>Service-account</ScopeChip>
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-tertiary)", marginTop: 8, lineHeight: 1.5 }}>
                  {isWorkspaceScope
                    ? "One shared credential is used for all members — for internal APIs that don't need user attribution."
                    : "Each member sets and uses their own credential."}
                </div>
              </Card>
            )}

            {isOwner && <FilterEditorCard wrapper={w} />}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card>
              <SectionTitle>Health</SectionTitle>
              <HealthIndicator slug={w.slug} rateLimitPerMin={w.rate_limit_per_min} isOwner={!!isOwner} />
            </Card>

            <Card>
              <SectionTitle>Endpoint</SectionTitle>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8, lineHeight: 1.5 }}>
                Attach this isolated endpoint to a host to expose only this API's tools.
              </div>
              <div style={endpointBox}>
                <code style={{ fontSize: 11, fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{endpointUrl}</code>
              </div>
              <button type="button" onClick={copyEndpoint} style={copyBtn}>
                <i className="ti ti-copy" style={{ fontSize: 13 }} aria-hidden="true" /> Copy URL
              </button>
            </Card>

            {/* Sprint 24 — shared Equipped-on-hires card (mirrors the MCP detail). */}
            <EquippedOnHires connectionKind="wrapped" connectionId={w.slug} />

            <Card>
              <SectionTitle>Details</SectionTitle>
              <DetailRow label="Auth" value={authLabel(w.auth_kind)} />
              <DetailRow label="Base URL" value={w.base_url} mono />
              <DetailRow label="Spec" value={w.openapi_spec_url ?? "—"} mono />
              <DetailRow label="Rate limit" value={`${w.rate_limit_per_min} / min per user`} />
              {isOwner && w.openapi_spec_url && (
                <button type="button" onClick={handleRefreshSpec} disabled={refreshSpec.isPending} style={refreshBtn}>
                  <i className="ti ti-refresh" style={{ fontSize: 12 }} aria-hidden="true" /> {refreshSpec.isPending ? "Refreshing…" : "Refresh spec"}
                </button>
              )}
            </Card>

            {isOwner && (
              <Card>
                <SectionTitle>Settings (owner)</SectionTitle>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
                  <span style={fieldLabel}>Name</span>
                  <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} style={inputStyle} />
                </label>
                <button
                  type="button"
                  onClick={handleSaveName}
                  disabled={!dirty || updateWrapper.isPending}
                  style={{ ...saveBtn, opacity: !dirty || updateWrapper.isPending ? 0.5 : 1 }}
                >
                  {updateWrapper.isPending ? "Saving…" : "Save"}
                </button>
                {!w.archived && (
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: "0.5px solid var(--color-border-secondary)" }}>
                    <button type="button" onClick={() => setConfirmRemove(true)} style={removeBtn}>
                      <i className="ti ti-trash" style={{ fontSize: 13 }} aria-hidden="true" /> Remove wrapped API
                    </button>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      )}

      {tab === "operations" && (
        <Card>
          <SectionTitle>Operations ({operations.length})</SectionTitle>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12, lineHeight: 1.5 }}>
            GET tools are always live. {isOwner ? "Enable write operations individually — they appear as MCP tools once on." : "An owner enables write operations."}
          </div>
          <OperationsTable slug={w.slug} operations={operations} isOwner={!!isOwner} />
        </Card>
      )}

      {tab === "mappings" && (
        <Card>
          <SectionTitle>Mappings — structured materialisation</SectionTitle>
          <MappingsList slug={w.slug} operations={operations} isOwner={!!isOwner} />
        </Card>
      )}

      {tab === "calls" && (
        <Card>
          <SectionTitle>Call log</SectionTitle>
          <CallLogTab slug={w.slug} active={tab === "calls"} />
        </Card>
      )}

      <ConfirmModal
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Remove wrapped API?"
        message={`"${w.name}" and its tools will disappear from tool lists. Credentials are retained (encrypted) in case you re-add it.`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleRemove}
      />
    </Frame>
  );
}

/**
 * Sprint 16e — owner-only subset filter editor. Shows the current filter, lets
 * the owner change it, and saves via update_wrapped_api (which re-synthesises the
 * tool list + emits notifications/tools/list_changed). Clearing a field clears
 * that dimension; clearing all wraps every operation again.
 */
function FilterEditorCard({ wrapper }: { wrapper: WrappedApiSummary }) {
  const update = useUpdateWrappedApi();
  const [pathPrefix, setPathPrefix] = useState("");
  const [operationIdsText, setOperationIdsText] = useState("");
  const [tag, setTag] = useState("");
  const [dirty, setDirty] = useState(false);

  const opIdsKey = (wrapper.operation_ids_filter ?? []).join(",");
  useEffect(() => {
    setPathPrefix(wrapper.path_prefix_filter ?? "");
    setOperationIdsText((wrapper.operation_ids_filter ?? []).join(", "));
    setTag(wrapper.tag_filter ?? "");
    setDirty(false);
  }, [wrapper.slug, wrapper.path_prefix_filter, wrapper.tag_filter, opIdsKey]);

  async function save() {
    if (pathPrefix.trim() && !pathPrefix.trim().startsWith("/")) {
      showToast('Path prefix must start with "/"', "error", true);
      return;
    }
    try {
      // Send all three so emptied inputs clear their dimension (DO treats "" / [] as clear).
      await update.mutateAsync({
        slug: wrapper.slug,
        path_prefix: pathPrefix.trim(),
        operation_ids: parseOperationIdsInput(operationIdsText),
        tag: tag.trim(),
      });
      showToast("Filter saved — tools re-synthesised");
      setDirty(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Couldn't save filter", "error", true);
    }
  }

  return (
    <Card>
      <SectionTitle>Filter operations (owner)</SectionTitle>
      <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 12, lineHeight: 1.5 }}>
        Wrapping <strong>{wrapper.operation_count_after_filter}</strong> of {wrapper.operation_count} operation
        {wrapper.operation_count === 1 ? "" : "s"}. Conditions combine with AND; clear all to wrap everything.
      </div>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        <span style={fieldLabel}>Path prefix</span>
        <input
          value={pathPrefix}
          onChange={(e) => { setPathPrefix(e.target.value); setDirty(true); }}
          placeholder="/api/v2/tickets"
          style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
        <span style={fieldLabel}>Operation IDs</span>
        <input
          value={operationIdsText}
          onChange={(e) => { setOperationIdsText(e.target.value); setDirty(true); }}
          placeholder="listTickets, showTicket"
          style={inputStyle}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 12 }}>
        <span style={fieldLabel}>Tag</span>
        <input
          value={tag}
          onChange={(e) => { setTag(e.target.value); setDirty(true); }}
          placeholder="Tickets"
          style={inputStyle}
        />
      </label>
      <button
        type="button"
        onClick={save}
        disabled={!dirty || update.isPending}
        style={{ ...saveBtn, opacity: !dirty || update.isPending ? 0.5 : 1 }}
      >
        {update.isPending ? "Saving…" : "Save filter"}
      </button>
    </Card>
  );
}

function authLabel(kind: string): string {
  switch (kind) {
    case "api_key": return "API key";
    case "oauth2_auth_code": return "OAuth 2.0 (Authorization Code)";
    case "oauth2_client_credentials": return "OAuth 2.0 (Client Credentials)";
    default: return kind;
  }
}

function Frame({ children, onBack }: { children: ReactNode; onBack: () => void }) {
  return (
    <div style={{ maxWidth: 900 }}>
      <button type="button" onClick={onBack} style={backBtn}>
        <i className="ti ti-arrow-left" style={{ fontSize: 14, marginRight: 6 }} aria-hidden="true" />
        Wrapped APIs
      </button>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 12 }}>{children}</div>;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 13,
        fontWeight: active ? 500 : 400,
        padding: "8px 2px",
        marginRight: 18,
        border: "none",
        background: "none",
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
        color: active ? "var(--color-text-primary)" : "var(--color-text-tertiary)",
        // Clean straight underline only — square corners + no focus ring, to
        // match the agent-profile tab bar (consistent tab styling app-wide).
        borderRadius: 0,
        outline: "none",
        borderBottom: active ? "2px solid var(--color-text-primary)" : "2px solid transparent",
      }}
    >
      {children}
    </button>
  );
}

function ScopeChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontSize: 12,
        padding: "5px 12px",
        borderRadius: "var(--border-radius-sm)",
        border: active ? "1px solid var(--color-text-info)" : "var(--kuvio-border-default)",
        background: active ? "var(--c-blue-50, var(--color-background-secondary))" : "var(--color-background-primary)",
        color: active ? "var(--color-text-info)" : "var(--color-text-secondary)",
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
        fontWeight: active ? 500 : 400,
      }}
    >
      {children}
    </button>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "5px 0", fontSize: 12 }}>
      <span style={{ width: 78, flexShrink: 0, color: "var(--color-text-tertiary)" }}>{label}</span>
      <span style={{ color: "var(--color-text-secondary)", fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)", wordBreak: "break-all", minWidth: 0 }}>
        {value}
      </span>
    </div>
  );
}

const tabBar: CSSProperties = {
  display: "flex",
  borderBottom: "0.5px solid var(--color-border-secondary)",
  marginBottom: 20,
};

const backBtn: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-secondary)",
  background: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  padding: 0,
  marginBottom: 16,
  display: "inline-flex",
  alignItems: "center",
};

const endpointBox: CSSProperties = {
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "8px 10px",
  marginBottom: 8,
};

const copyBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const refreshBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  marginTop: 8,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const fieldLabel: CSSProperties = { fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)" };

const inputStyle: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  fontSize: 13,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};

const saveBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const removeBtn: CSSProperties = {
  fontSize: 13,
  padding: "6px 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--c-red-600, var(--color-text-primary))",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const archivedTag: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-sm)",
  padding: "2px 8px",
};
